import { Link } from 'react-router-dom';
import type { DashboardNeedsAttentionIssue } from '@overlayqa/shared';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert02Icon } from '@hugeicons/core-free-icons';
import { ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { SeverityBadge } from '../shared/badges/SeverityBadge';

interface NeedsAttentionListProps {
  items: DashboardNeedsAttentionIssue[];
  variant?: 'default' | 'overview';
}

function ageLabel(ageDays: number): string {
  return ageDays <= 0 ? 'today' : `${ageDays}d`;
}

export function NeedsAttentionList({ items, variant = 'default' }: NeedsAttentionListProps) {
  const overview = variant === 'overview';
  const visibleItems = items.slice(0, 3);
  return (
    <section id="needs-attention" aria-label="Needs attention" className={overview ? 'min-w-0 flex flex-col' : 'card-sm flex flex-col'}>
      {overview ? <>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-700">Needs attention</h2>
          <p className="text-xs text-gray-500 tabular-nums">{visibleItems.length} shown</p>
        </div>
        {/* dashboard.ts:191-198 ranks active issues by severity then age and
            caps the result at five. Emily requested at most three visible
            issues; this is a shortlist, not an active total. */}
        <p className="mt-2 mb-3 text-xs text-gray-500">Active issues · highest severity, then oldest</p>
      </> : <h2 className="section-label mb-3 flex items-center justify-between gap-2">
        Needs Attention
        <HugeiconsIcon icon={Alert02Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />
      </h2>}
      <div className="flex-1">
        {visibleItems.length === 0 ? (
          <div className="empty-state-compact flex flex-col items-center justify-center gap-1 h-full text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mb-1">
              <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            You're all caught up
            <span className="text-xs text-gray-400">No open issues need attention right now</span>
          </div>
        ) : (
          <ul className={overview ? 'divide-y divide-gray-100' : 'space-y-1'}>
            {visibleItems.map((issue) => (
              <li key={issue.id}>
                <Link
                  to={`/issues/${issue.displayId ?? issue.id}`}
                  className={`group flex items-center gap-3 -mx-2 px-2 rounded-md hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${overview ? 'py-3' : 'py-2'}`}
                >
                  <span className="flex-shrink-0 w-[68px]">
                    {overview ? <span className="flex items-center gap-2 text-xs text-gray-500 capitalize"><span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: `var(--color-severity-chart-${issue.severity})` }} aria-hidden="true" />{issue.severity}</span> : <SeverityBadge severity={issue.severity} />}
                  </span>
                  <span className={overview ? 'min-w-0 flex-1' : 'min-w-0 flex-1 flex items-center gap-2'}>
                    <span className={overview ? 'block text-sm font-medium text-gray-700 line-clamp-2 break-words' : 'min-w-0 flex-1 truncate text-sm font-medium text-gray-900'}>{issue.title}</span>
                    {overview ? <span className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <span className="min-w-0 truncate">{issue.projectName}</span>
                      <span aria-hidden="true">·</span>
                      <span className="shrink-0 whitespace-nowrap">{issue.ageDays <= 0 ? 'Today' : `${ageLabel(issue.ageDays)} old`}</span>
                    </span> : <span className="flex-shrink-0 whitespace-nowrap text-xs text-gray-400">{issue.projectName} &middot; {ageLabel(issue.ageDays)}</span>}
                  </span>
                  <HugeiconsIcon
                    icon={ArrowRight01Icon}
                    size={16}
                    strokeWidth={2}
                    color="currentColor"
                    className="w-4 h-4 text-gray-300 flex-shrink-0 group-hover:text-gray-400"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function NeedsAttentionListSkeleton({ variant = 'default' }: { variant?: 'default' | 'overview' }) {
  return (
    <section aria-label="Needs attention loading" className={variant === 'overview' ? 'min-w-0 flex flex-col' : 'card-sm flex flex-col'}>
      <div className="mb-3">
        <div className="h-2.5 rounded w-28 skeleton-shimmer" />
      </div>
      <div className="flex-1 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-3 rounded w-14 skeleton-shimmer" />
            <div className="flex-1 space-y-2">
              <div className="h-3 rounded w-3/4 skeleton-shimmer" />
              <div className="h-2.5 rounded w-1/3 skeleton-shimmer" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
